import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const schemaJson = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Organization',
      name: '开元体育',
    },
    publisher: {
      '@type': 'Organization',
      name: '开元体育',
      url: 'https://www.kaiyuansports.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.kaiyuansports.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 开元体育`}
        description={article.excerpt}
        keywords={`开元体育,${article.tags.join(',')},${article.categoryName}`}
        canonicalUrl={`https://www.kaiyuansports.com/article/${article.slug}`}
        ogType="article"
        schemaJson={schemaJson}
      />

      <article className="w-full px-4 md:px-6 lg:px-8 pt-20 pb-4 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: article.categoryName, to: `/category/${articles.find((a) => a.categoryName === article.categoryName)?.slug || ''}` },
              { label: article.title },
            ]}
          />
        </div>
      </article>

      <section className="w-full px-4 md:px-6 lg:px-8 pb-10 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="relative w-full h-56 md:h-80 lg:h-96 rounded-2xl overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
          </div>

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="px-2.5 py-0.5 text-xs font-medium bg-accent-500 text-background-50 rounded-full">
              {article.categoryName}
            </span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-0.5 text-xs font-medium bg-background-200/70 text-foreground-600 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 font-heading mb-4 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-xs text-foreground-400 mb-8 pb-8 border-b border-background-200/70">
            <span>{article.publishedAt}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views.toLocaleString()} {t('featured.views')}
            </span>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 lg:px-8 py-8 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-10 prose-h2:mb-4
              prose-p:text-sm md:prose-p:text-base prose-p:leading-relaxed prose-p:mb-5
              prose-strong:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>
      </section>

      {relatedArticles.length > 0 && (
        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8">
              {t('article.related')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group bg-background-50 border border-background-200/80 rounded-xl overflow-hidden hover:border-accent-300/60 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-40 md:h-44 overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {ra.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{ra.publishedAt}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {ra.views.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}